/**
 * RowFooterSlot - Renders controls in the row-footer location.
 *
 * This slot appears after each row (full width) and could be used for
 * row-level controls or metadata.
 */

import React from 'react';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type ControlDefinition, type GridRow} from '../types';

export type RowFooterSlotProps = {
  controls: ControlDefinition[];
  row: GridRow;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
};

/**
 * Renders controls in the row-footer location.
 *
 * All controls are rendered through ControlRenderer, which delegates to the
 * appropriate control component that handles its own rendering.
 *
 * @param props - Row footer slot props
 * @returns Rendered row footer or null if no controls
 */
export const RowFooterSlot: React.FC<RowFooterSlotProps> = ({
  controls,
  config,
  updateConfig,
  stepMeta,
}) => {
  if (controls.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-4">
      {controls.map((control, idx) => (
        <ControlRenderer
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          key={`row-footer-control-${idx}`}
          control={control}
          index={idx}
          config={config}
          updateConfig={updateConfig}
          stepMeta={stepMeta}
        />
      ))}
    </div>
  );
};
